import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import api from '../../../api/api';
import { objectStorageApi } from '../../../api/objectStorage';
import HomeEventVisibilityControl from './HomeEventVisibilityControl';
import useAdminDraftGuard from '../useAdminDraftGuard';

const emptyForm = () => ({
  title: '',
  titleEn: '',
  description: '',
  descriptionEn: '',
  imageUrl: '',
  actionLabel: '',
  actionLabelEn: '',
  destinationUrl: '',
  isPinned: false,
  showAdMark: true,
  isActive: true,
});

export default function EventAdManagement() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const draft = useAdminDraftGuard({ form, file }, { enabled: form !== null, busy });
  const reload = async () => {
    setError('');
    setLoading(true);
    try {
      const response = await api.getAuth('/admin/ads/events');
      setItems(response.data.data);
    } catch {
      setError('이벤트 목록을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    void reload();
  }, []);
  useEffect(() => {
    if (!file) {
      setPreview('');
      return undefined;
    }
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);
  const edit = (item) => {
    if (!draft.confirmDiscard()) return;
    draft.resetBaseline();
    setForm(item || emptyForm());
    setFile(null);
    setError('');
  };
  const field = (name) => ({
    value: form[name] || '',
    onChange: (event) => setForm({ ...form, [name]: event.target.value }),
  });
  const remove = async () => {
    if (busy || !deleteTarget) return;
    setBusy(true);
    setError('');
    try {
      await api.deleteAuth(`/admin/ads/events/${deleteTarget.id}`);
      setItems((current) => current.filter((item) => item.id !== deleteTarget.id));
      setDeleteTarget(null);
      await reload();
    } catch (failure) {
      setError(failure.response?.data?.msg || '이벤트를 삭제하지 못했습니다. 다시 시도해주세요.');
    } finally {
      setBusy(false);
    }
  };
  const save = async (event) => {
    event.preventDefault();
    setError('');
    setBusy(true);
    try {
      const payload = { ...form };
      delete payload.id;
      if (file) {
        const body = new FormData();
        body.append('image', file);
        const result = await objectStorageApi.uploadImage(body);
        if (!result.data?.url) throw new Error('이미지 업로드 결과가 없습니다.');
        payload.imageUrl = result.data.url;
        setForm((current) => ({ ...current, imageUrl: payload.imageUrl }));
        setFile(null);
      }
      if (!payload.imageUrl) throw new Error('대표 이미지를 업로드해주세요.');
      const url = new URL(payload.destinationUrl);
      if (url.protocol !== 'https:' || url.username || url.password)
        throw new Error('응모 URL은 인증 정보 없는 HTTPS 주소여야 합니다.');
      if (form.id) await api.putAuth(`/admin/ads/events/${form.id}`, payload);
      else await api.postAuth('/admin/ads/events', payload);
      setForm(null);
      await reload();
    } catch (failure) {
      setError(failure.response?.data?.msg || failure.message || '저장하지 못했습니다.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <Stack spacing={2}>
      <Typography variant="h6">이벤트 관리</Typography>
      <HomeEventVisibilityControl />
      <Typography>
        자체 이벤트와 광고 이벤트를 등록합니다. 고정된 항목이 먼저, 같은 그룹에서는 최신 등록순으로
        노출됩니다.
      </Typography>
      {!form && !deleteTarget && error && (
        <Alert severity="error" action={<Button onClick={reload}>재시도</Button>}>
          {error}
        </Alert>
      )}
      <Button variant="contained" disabled={busy} onClick={() => edit(null)}>
        이벤트 등록
      </Button>
      {loading ? (
        <Typography>불러오는 중…</Typography>
      ) : (
        !items.length && <Typography>등록된 이벤트가 없습니다.</Typography>
      )}
      {items.map((item) => (
        <Stack
          key={item.id}
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          alignItems={{ xs: 'stretch', sm: 'center' }}
        >
          <Box
            component="img"
            src={item.imageUrl}
            alt=""
            sx={{ width: 112, aspectRatio: '14 / 9', objectFit: 'cover', borderRadius: 1 }}
          />
          <Box sx={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}>
            <Typography>{item.title || item.actionLabel}</Typography>
            <Typography variant="caption">
              {item.isActive ? '공개' : '비공개'} · {item.isPinned ? '상단 고정' : '일반'} ·{' '}
              {item.showAdMark ? 'AD 표시' : 'AD 미표기'}
            </Typography>
          </Box>
          <Button disabled={busy} onClick={() => edit(item)}>
            수정
          </Button>
          <Button
            color="error"
            disabled={busy}
            onClick={() => {
              setError('');
              setDeleteTarget(item);
            }}
          >
            삭제
          </Button>
        </Stack>
      ))}
      <Dialog
        open={!!deleteTarget}
        onClose={() => {
          if (!busy) setDeleteTarget(null);
        }}
        aria-labelledby="event-delete-title"
      >
        <DialogTitle id="event-delete-title">이벤트 삭제</DialogTitle>
        <DialogContent>
          <Typography>
            ‘{deleteTarget?.title || deleteTarget?.actionLabel}’ 이벤트를 삭제할까요?
          </Typography>
          <Typography>
            이벤트 목록과 홈 노출에서 제거되며 되돌릴 수 없습니다. 업로드된 이미지 파일은 삭제하지
            않습니다.
          </Typography>
          {error && <Alert severity="error">{error}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button disabled={busy} onClick={() => setDeleteTarget(null)}>
            취소
          </Button>
          <Button color="error" disabled={busy} onClick={remove}>
            {busy ? '삭제 중…' : '삭제 확인'}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={!!form}
        fullWidth
        maxWidth="sm"
        onClose={() => {
          if (draft.confirmDiscard()) setForm(null);
        }}
      >
        {form && (
          <Box component="form" aria-label="이벤트 편집" onSubmit={save}>
            <DialogTitle>{form.id ? '이벤트 수정' : '이벤트 등록'}</DialogTitle>
            <DialogContent>
              <Stack
                component="fieldset"
                disabled={busy}
                spacing={2}
                sx={{ border: 0, p: 0, m: 0, pt: 1 }}
              >
                {error && <Alert severity="error">{error}</Alert>}
                <Button component="label" variant="outlined" disabled={busy}>
                  대표 이미지 업로드
                  <input
                    hidden
                    type="file"
                    aria-label="대표 이미지 파일"
                    accept="image/png,image/jpeg,image/webp"
                    onChange={(event) => {
                      const next = event.target.files?.[0];
                      event.target.value = '';
                      if (!next) return;
                      if (
                        !['image/png', 'image/jpeg', 'image/webp'].includes(next.type) ||
                        next.size > 5 * 1024 * 1024
                      ) {
                        setError('5MB 이하 PNG/JPG/WebP 이미지를 선택하세요.');
                        return;
                      }
                      setFile(next);
                      setError('');
                    }}
                  />
                </Button>
                <Typography variant="caption">
                  이미지와 카드 전체 14:9 · 권장 1400×900px · 하단 44pt 버튼이 이미지 위에 겹칩니다.
                  중요한 이미지는 하단을 피하고, 제목/설명은 이미지에 넣지 않아도 됩니다.
                </Typography>
                <TextField
                  label="제목 (이미지 안)"
                  helperText="선택 입력"
                  inputProps={{ maxLength: 160 }}
                  {...field('title')}
                />
                <TextField
                  label="설명 (이미지 안)"
                  helperText="선택 입력"
                  inputProps={{ maxLength: 240 }}
                  {...field('description')}
                />
                <TextField
                  label="주황색 버튼 문구"
                  required
                  inputProps={{ maxLength: 120 }}
                  placeholder="음감회 이벤트 응모하기"
                  {...field('actionLabel')}
                />
                <TextField
                  label="외부 응모 URL"
                  type="url"
                  required
                  inputProps={{ maxLength: 2048 }}
                  {...field('destinationUrl')}
                />
                <TextField
                  label="영문 제목 (선택)"
                  inputProps={{ maxLength: 160 }}
                  {...field('titleEn')}
                />
                <TextField
                  label="영문 설명 (선택)"
                  inputProps={{ maxLength: 240 }}
                  {...field('descriptionEn')}
                />
                <TextField
                  label="영문 버튼 문구 (선택)"
                  inputProps={{ maxLength: 120 }}
                  {...field('actionLabelEn')}
                />
                {['isPinned', 'showAdMark', 'isActive'].map((name, index) => (
                  <FormControlLabel
                    key={name}
                    label={['상단 고정', 'AD 마크 표시', '앱에 공개'][index]}
                    control={
                      <Checkbox
                        checked={form[name]}
                        onChange={(event) => setForm({ ...form, [name]: event.target.checked })}
                      />
                    }
                  />
                ))}
                <Typography variant="caption">
                  미리보기 (실제 앱 글꼴과 줄바꿈은 기기에 따라 다를 수 있습니다)
                </Typography>
                <Box
                  data-testid="event-preview"
                  sx={{ position: 'relative', overflow: 'hidden', borderRadius: '18px' }}
                >
                  <Box
                    sx={{
                      position: 'relative',
                      aspectRatio: '14 / 9',
                      backgroundColor: '#ddd',
                      backgroundImage: `url("${preview || form.imageUrl}")`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                    }}
                  >
                    {(form.title.trim() || form.description.trim()) && (
                      <Box
                        sx={{
                          position: 'absolute',
                          inset: 0,
                          background:
                            'linear-gradient(rgba(0,0,0,0),rgba(0,0,0,.12) 50%,rgba(0,0,0,.78))',
                        }}
                      />
                    )}
                    <Box
                      sx={{ position: 'absolute', left: 16, right: 16, bottom: 56, color: '#fff' }}
                    >
                      {form.title.trim() && (
                        <Typography
                          noWrap
                          sx={{
                            color: '#FFFFFF',
                            fontSize: 18,
                            lineHeight: '24px',
                            fontWeight: 600,
                            letterSpacing: '-0.4px',
                            transform: 'translateY(1px)',
                          }}
                        >
                          {form.title}
                        </Typography>
                      )}
                      {form.description.trim() && (
                        <Typography
                          sx={{
                            fontSize: 12,
                            lineHeight: '17px',
                            mt: form.title.trim() ? '3.5px' : 0,
                            color: 'rgba(255,255,255,.8)',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                          }}
                        >
                          {form.description}
                        </Typography>
                      )}
                    </Box>
                    {form.showAdMark && (
                      <Box
                        sx={{
                          position: 'absolute',
                          right: 16,
                          top: 12,
                          color: '#fff',
                          fontSize: 10,
                        }}
                      >
                        AD
                      </Box>
                    )}
                  </Box>
                  <Box
                    data-testid="event-preview-action"
                    sx={{
                      position: 'absolute',
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: '#F16438',
                      color: '#fff',
                      px: 2,
                      height: 44,
                      boxSizing: 'border-box',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 1,
                    }}
                  >
                    <span
                      style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                    >
                      {form.actionLabel || '버튼 문구'}
                    </span>
                    <span style={{ flexShrink: 0 }}>›</span>
                  </Box>
                </Box>
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button
                disabled={busy}
                onClick={() => {
                  if (draft.confirmDiscard()) setForm(null);
                }}
              >
                취소
              </Button>
              <Button type="submit" disabled={busy}>
                {busy ? '저장 중…' : '저장'}
              </Button>
            </DialogActions>
          </Box>
        )}
      </Dialog>
    </Stack>
  );
}
